import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  PlanningCycle,
  PlanningObjective,
  PlanningWorkItem,
  PlanningWorkItemUpsertPayload,
} from '../../services/strategicPlanningApi';
import { formatPlanningObjectiveKind } from '../../types/PlanningObjectiveKind';
import {
  canEditPlanningTaskOwner,
  getPlanningStatusReasonCopy,
  requiresPlanningStatusReason,
} from '../../types/PlanningTaskStatus';
import { PlanningCancellationReasonField } from './PlanningCancellationReasonField';
import { CollaboratorAutocomplete } from '../ui/CollaboratorAutocomplete';
import { ModalPortalShell } from '../ui/ModalPortalShell';
import { RequiredMark } from '../ui/RequiredMark';
import { fetchMyTasksScope } from '../../services/myTasksApi';

type PlanningWorkItemFormModalProps = {
  open: boolean;
  kind: 'Meta' | 'Marco' | 'Tarefa';
  cycles: PlanningCycle[];
  objectives?: PlanningObjective[];
  parentOptions?: PlanningWorkItem[];
  objectiveId?: string;
  parentWorkItemId?: string;
  defaultCycleId?: string;
  defaultPlannedStartDate?: string;
  defaultPlannedEndDate?: string;
  defaultSortOrder?: number;
  initial?: PlanningWorkItem | null;
  onClose: () => void;
  onSubmit: (payload: PlanningWorkItemUpsertPayload) => Promise<void>;
};

const PRIORITIES = [
  { value: 'Low', label: 'Baixa' },
  { value: 'Medium', label: 'Média' },
  { value: 'High', label: 'Alto' },
  { value: 'Critical', label: 'Crítica' },
];

const KIND_LABELS: Record<PlanningWorkItemFormModalProps['kind'], string> = {
  Meta: 'meta',
  Marco: 'marco',
  Tarefa: 'tarefa',
};

const WORK_ITEM_STATUSES = [
  { value: 'Planned', label: 'Planejada' },
  { value: 'InProgress', label: 'Em andamento' },
  { value: 'Completed', label: 'Concluída' },
  { value: 'Cancelled', label: 'Cancelada' },
];

const META_EXTRA_STATUSES = [
  { value: 'NotStarted', label: 'Não iniciada' },
  { value: 'Abandoned', label: 'Abandonada' },
];

export function PlanningWorkItemFormModal({
  open,
  kind,
  cycles,
  objectives = [],
  parentOptions = [],
  objectiveId,
  parentWorkItemId,
  defaultCycleId,
  defaultPlannedStartDate,
  defaultPlannedEndDate,
  defaultSortOrder,
  initial,
  onClose,
  onSubmit,
}: PlanningWorkItemFormModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [plannedStartDate, setPlannedStartDate] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [selectedObjectiveId, setSelectedObjectiveId] = useState('');
  const [selectedParentWorkItemId, setSelectedParentWorkItemId] = useState('');
  const [status, setStatus] = useState('Planned');
  const [cancellationReason, setCancellationReason] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [ownerUserId, setOwnerUserId] = useState('');
  const [ownerUserName, setOwnerUserName] = useState('');
  const canChangeOwner = !initial
    || canEditPlanningTaskOwner(initial.status, initial.executionFinishedAt);

  const objectiveChoices = useMemo(() => {
    let result = objectives;

    if (cycleId) {
      result = result.filter((objective) => objective.cycleId === cycleId);
    }

    return result;
  }, [objectives, cycleId]);

  const parentChoices = useMemo(() => {
    let result = parentOptions;

    if (selectedObjectiveId) {
      result = result.filter((item) => item.objectiveId === selectedObjectiveId);
    }

    if (cycleId) {
      result = result.filter((item) => !item.cycleId || item.cycleId === cycleId);
    }

    return result;
  }, [parentOptions, selectedObjectiveId, cycleId]);

  useEffect(() => {
    if (!open) return;

    setTitle(initial?.title ?? '');
    setDescription(initial?.description ?? '');
    setPriority(initial?.priority ?? 'Medium');
    setPlannedStartDate(initial?.plannedStartDate ?? defaultPlannedStartDate ?? '');
    setPlannedEndDate(initial?.plannedEndDate ?? defaultPlannedEndDate ?? '');
    setCycleId(initial?.cycleId ?? defaultCycleId ?? '');
    setSelectedObjectiveId(initial?.objectiveId ?? objectiveId ?? '');
    setSelectedParentWorkItemId(initial?.parentWorkItemId ?? parentWorkItemId ?? '');
    setStatus(initial?.status ?? 'Planned');
    setCancellationReason(initial?.cancellationReason ?? '');
    setOwnerUserId(initial?.ownerUserId ?? '');
    setOwnerUserName(initial?.ownerUserName ?? '');
    setFormError('');
  }, [
    open,
    initial,
    defaultCycleId,
    defaultPlannedStartDate,
    defaultPlannedEndDate,
    objectiveId,
    parentWorkItemId,
  ]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    void fetchMyTasksScope()
      .then((scope) => {
        if (cancelled) return;
        setOwnerUserId((current) => {
          if (current) return current;
          return scope.defaultUsers.find((user) => user.isSelf)?.userId ?? '';
        });
        setOwnerUserName((current) => {
          if (current) return current;
          return scope.defaultUsers.find((user) => user.isSelf)?.name ?? '';
        });
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const kindLabel = KIND_LABELS[kind];
  const requiresObjective = kind === 'Meta' || (kind === 'Tarefa' && !selectedParentWorkItemId);
  const requiresParent = kind === 'Marco';
  const statusOptions = kind === 'Meta'
    ? [
        ...WORK_ITEM_STATUSES.slice(0, 3),
        ...META_EXTRA_STATUSES,
        WORK_ITEM_STATUSES[3],
      ]
    : WORK_ITEM_STATUSES;
  const needsStatusReason = requiresPlanningStatusReason(status);
  const statusReasonCopy = getPlanningStatusReasonCopy(status);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError('');

    if (needsStatusReason && !cancellationReason.trim()) {
      setFormError(
        status === 'NotStarted'
          ? 'Informe o motivo de a meta não ter sido iniciada.'
          : status === 'Abandoned'
            ? 'Informe o motivo do abandono da meta.'
            : 'Informe o motivo do cancelamento.',
      );
      return;
    }

    if (requiresObjective && !selectedObjectiveId) {
      setFormError(
        kind === 'Meta'
          ? 'Selecione o objetivo ao qual a meta pertence.'
          : 'Selecione o objetivo da tarefa.',
      );
      return;
    }

    if (requiresParent && !selectedParentWorkItemId) {
      setFormError('Selecione a meta à qual o marco pertence.');
      return;
    }

    if (!ownerUserId) {
      setFormError(`Selecione o responsável d${kind === 'Marco' ? 'o' : 'a'} ${kindLabel}.`);
      return;
    }

    const parent = parentChoices.find((item) => item.id === selectedParentWorkItemId)
      ?? parentOptions.find((item) => item.id === selectedParentWorkItemId);
    const resolvedObjectiveId = selectedObjectiveId
      || parent?.objectiveId
      || undefined;

    if (kind === 'Meta' && !resolvedObjectiveId) {
      setFormError('Selecione o objetivo ao qual a meta pertence.');
      return;
    }

    setSaving(true);
    try {
      await onSubmit({
        kind,
        title: title.trim(),
        description: description.trim() || undefined,
        status: initial ? status : undefined,
        cancellationReason: needsStatusReason ? cancellationReason.trim() : undefined,
        priority,
        sortOrder: initial?.sortOrder ?? defaultSortOrder ?? 0,
        plannedStartDate: plannedStartDate || undefined,
        plannedEndDate: plannedEndDate || undefined,
        cycleId: cycleId || undefined,
        objectiveId: resolvedObjectiveId,
        parentWorkItemId: selectedParentWorkItemId || undefined,
        ownerUserId: canChangeOwner ? ownerUserId : undefined,
        sprintId: initial?.sprintId ?? undefined,
        tags: initial?.tags ?? undefined,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortalShell open={open}>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => void handleSubmit(event)}
        >
          <div className="shrink-0 border-b border-secondary-200 px-5 py-4">
            <h2 className="erp-card-title">
              {initial ? `Editar ${kindLabel}` : `Nova ${kindLabel}`}
            </h2>
          </div>

          <div className="erp-card-body min-h-0 flex-1 space-y-4 overflow-y-auto">
          {formError ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {formError}
            </p>
          ) : null}

          <label className="block">
            <span className="erp-label">Título</span>
            <input
              className="erp-input"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              maxLength={300}
            />
          </label>

          <label className="block">
            <span className="erp-label">Descrição</span>
            <textarea
              className="erp-input min-h-[80px]"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={4000}
            />
          </label>

          <label className="block">
            <span className="erp-label">
              Responsável <RequiredMark />
            </span>
            {canChangeOwner ? (
              <p className="mb-2 text-xs text-secondary-500">
                Clique para buscar. A escolha é aplicada ao sair do campo.
              </p>
            ) : (
              <p className="mb-2 text-xs text-secondary-500">
                Item encerrado: o responsável não pode ser alterado.
              </p>
            )}
            <CollaboratorAutocomplete
              valueUserId={ownerUserId || null}
              valueUserName={ownerUserName || null}
              disabled={saving || !canChangeOwner}
              onCommit={(user) => {
                setOwnerUserId(user?.userId ?? '');
                setOwnerUserName(user?.name ?? '');
              }}
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="erp-label">Prioridade</span>
              <select
                className="erp-input"
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
              >
                {PRIORITIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="erp-label">Ciclo</span>
              <select
                className="erp-input"
                value={cycleId}
                onChange={(event) => {
                  setCycleId(event.target.value);
                  setSelectedObjectiveId('');
                  if (kind === 'Marco') {
                    setSelectedParentWorkItemId('');
                  }
                }}
              >
                <option value="">Sem ciclo</option>
                {cycles.map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {kind === 'Meta' || kind === 'Tarefa' ? (
            <label className="block">
              <span className="erp-label">
                Objetivo
                {requiresObjective ? (
                  <>
                    {' '}
                    <RequiredMark />
                  </>
                ) : null}
              </span>
              <select
                className="erp-input"
                value={selectedObjectiveId}
                onChange={(event) => {
                  const nextObjectiveId = event.target.value;
                  setSelectedObjectiveId(nextObjectiveId);
                  const objective = objectives.find((item) => item.id === nextObjectiveId);
                  if (objective?.cycleId) {
                    setCycleId(objective.cycleId);
                  }
                }}
                required={requiresObjective}
              >
                <option value="">Selecione o objetivo</option>
                {objectiveChoices.map((objective) => (
                  <option key={objective.id} value={objective.id}>
                    {objective.title} ({formatPlanningObjectiveKind(objective.kind)})
                  </option>
                ))}
              </select>
              {kind === 'Meta' && objectiveChoices.length === 0 ? (
                <p className="mt-1 text-xs text-secondary-500">
                  Não há objetivos no ciclo selecionado. Crie um objetivo antes.
                </p>
              ) : null}
            </label>
          ) : null}

          {kind === 'Marco' ? (
            <label className="block">
              <span className="erp-label">Meta pai <RequiredMark /></span>
              <select
                className="erp-input"
                value={selectedParentWorkItemId}
                onChange={(event) => {
                  const nextParentId = event.target.value;
                  setSelectedParentWorkItemId(nextParentId);
                  const parent = parentOptions.find((item) => item.id === nextParentId);
                  if (parent?.objectiveId) {
                    setSelectedObjectiveId(parent.objectiveId);
                  }
                  if (parent?.cycleId) {
                    setCycleId(parent.cycleId);
                  }
                }}
                required
              >
                <option value="">Selecione a meta</option>
                {parentChoices.map((meta) => (
                  <option key={meta.id} value={meta.id}>
                    {meta.title}
                  </option>
                ))}
              </select>
              {parentChoices.length === 0 ? (
                <p className="mt-1 text-xs text-secondary-500">
                  Não há metas disponíveis. Crie uma meta vinculada a um objetivo antes.
                </p>
              ) : null}
            </label>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="erp-label">Início</span>
              <input
                type="date"
                className="erp-input"
                value={plannedStartDate}
                onChange={(event) => setPlannedStartDate(event.target.value)}
              />
            </label>
            <label className="block">
              <span className="erp-label">Fim</span>
              <input
                type="date"
                className="erp-input"
                value={plannedEndDate}
                onChange={(event) => setPlannedEndDate(event.target.value)}
                readOnly={kind === 'Tarefa' && Boolean(initial?.executionStartedAt)}
                disabled={kind === 'Tarefa' && Boolean(initial?.executionStartedAt)}
                title={
                  kind === 'Tarefa' && initial?.executionStartedAt
                    ? 'Após iniciar a execução, use Estender prazo'
                    : undefined
                }
              />
              {kind === 'Tarefa' && initial?.executionStartedAt ? (
                <span className="mt-1 block text-xs text-secondary-500">
                  Após iniciar a execução, use a ação Estender prazo.
                </span>
              ) : null}
            </label>
          </div>

          {initial ? (
            <label className="block">
              <span className="erp-label">Status</span>
              <select
                className="erp-input"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {initial && needsStatusReason ? (
            <PlanningCancellationReasonField
              value={cancellationReason}
              onChange={setCancellationReason}
              label={statusReasonCopy.label}
              placeholder={statusReasonCopy.placeholder}
            />
          ) : null}
          </div>

          <div className="erp-btn-actions shrink-0 border-t border-secondary-200 bg-white px-5 py-4">
            <button type="button" className="erp-btn-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="erp-btn-primary" disabled={saving}>
              {saving ? 'Salvando…' : initial ? 'Salvar' : `Criar ${kindLabel}`}
            </button>
          </div>
        </form>
    </ModalPortalShell>
  );
}
